import { useTranslation } from 'react-i18next'
import { Plus, Tag, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  FORM_CONTROL_BASE_CLASS,
  FORM_CONTROL_FOCUS_CLASS as fieldFocusClass,
  FORM_CONTROL_SELECT_OPEN_CLASS as selectOpenStateClass,
} from '@/components/ui/form-control-styles'
import { CurrencyNumberInput } from '@/components/ui/currency-input'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTaxRateOptions } from '@/hooks/useSettings'
import { formatCurrency } from '@/lib/format'
import { defaultFeeImageUrl } from '@/lib/optionalFeeIcons'
import { TAX_RATES } from '@/lib/lineItems'
import { computeChargesTotal } from '@/lib/optionalCharges'
import { cn } from '@/lib/utils'
import {
  OPTIONAL_CHARGE_TYPES,
  type OptionalCharge,
  type OptionalChargeType,
} from './types'

export interface OptionalChargesSectionProps {
  /** Controlled list of surcharges. */
  value: OptionalCharge[]
  onChange: (charges: OptionalCharge[]) => void
  /** Validation message surfaced above the rows (e.g. missing custom description). */
  error?: string | null
  disabled?: boolean
  /** Hide the built-in section heading when the parent renders one (e.g. quote form). */
  hideHeading?: boolean
  /** Decimal places for currency display (default 0). */
  fractionDigits?: number
  /** Default VAT rate used when a new optional fee row is created. */
  vatRate?: number
  /** Optional synthetic row rendered under optional fees for quote-level discounts. */
  tradeDiscountRow?: {
    title: string
    description: string
    amount: number
    onRemove?: () => void
  } | null
  /** Override the default inline add-row action (quote form opens a drawer). */
  onAddChargeClick?: () => void
  /** Persisted fee presets loaded from the backend for quick reuse. */
  quickPresets?: Array<{
    id: string
    type: OptionalChargeType
    label: string
    amount?: number
    description?: string
    image_file_id?: string | null
    image_url?: string | null
  }>
}

type QuickPreset = {
  id: string
  type: OptionalChargeType
  label: string
  amount?: number
  description?: string
  image_file_id?: string | null
  image_url?: string | null
}

function computeAmountFromLineTotal(
  lineTotal: number,
  taxRate: number
): number {
  const taxMultiplier = 1 + taxRate / 100
  if (taxMultiplier <= 0) return 0
  return Math.round((lineTotal / taxMultiplier) * 100) / 100
}

function emptyCharge(vatRate: number): OptionalCharge {
  return {
    type: 'emergency',
    description: '',
    image_file_id: null,
    image_url: null,
    amount: 0,
    tax_rate: vatRate,
  }
}

const figmaFieldBaseClass = cn(
  'h-10 rounded-xl border px-4 py-3 leading-[1.4] shadow-none',
  FORM_CONTROL_BASE_CLASS
)
const figmaFieldClass = cn(figmaFieldBaseClass, fieldFocusClass)
const figmaSelectClass = cn(figmaFieldBaseClass, selectOpenStateClass)
const addButtonClass =
  'h-10 rounded-xl border-dashed border-[#b0b5c3] bg-white py-2 px-3 text-xs font-medium text-[#171a20] hover:bg-[#fbfbfc]'
const quickPresetButtonClass =
  'h-10 rounded-xl border border-[#e7e9ef] bg-white py-2 px-3 text-xs font-medium text-[#171a20] hover:bg-[#fbfbfc]'

// Controlled list of optional charges / surcharges shared by Create/Edit quote
// (#29) and Create invoice (#42). Each row picks a type, an amount, and an
// optional description (required when the type is "custom"); the running total
// is auto-calculated.
export function OptionalChargesSection({
  value,
  onChange,
  error,
  disabled,
  hideHeading = false,
  fractionDigits = 0,
  vatRate = 0,
  tradeDiscountRow = null,
  onAddChargeClick,
  quickPresets,
}: OptionalChargesSectionProps) {
  const { t } = useTranslation('shared')
  const { data: taxOptions } = useTaxRateOptions()
  const resolvedVatRate = Number.isFinite(vatRate) ? vatRate : 0
  const showTaxRate = true
  const defaultTaxRate = resolvedVatRate

  const fmt = (value: number) => formatCurrency(value, 'EUR', fractionDigits)

  const update = (idx: number, patch: Partial<OptionalCharge>) =>
    onChange(value.map((c, i) => (i === idx ? { ...c, ...patch } : c)))

  const add = () => onChange([...value, emptyCharge(resolvedVatRate)])
  const remove = (idx: number) => onChange(value.filter((_, i) => i !== idx))
  const appendPreset = ({
    type,
    amount,
    description,
    image_file_id,
    image_url,
  }: Pick<QuickPreset, 'type'> & {
    amount?: number
    description?: string
    image_file_id?: string | null
    image_url?: string | null
  }) =>
    onChange([
      ...value,
      {
        type,
        description,
        image_file_id: image_file_id ?? null,
        image_url: image_url ?? null,
        amount: amount ?? 0,
        tax_rate: resolvedVatRate,
      },
    ])
  const detailGridClass =
    'grid grid-cols-[minmax(280px,1fr)_144px_144px_144px_40px] items-center border-b border-[#e7e9ef] last:border-b-0'
  const detailHeaderGridClass =
    'grid grid-cols-[minmax(280px,1fr)_144px_144px_144px_40px] border-b border-[#e7e9ef] bg-[#fbfbfc]'
  const detailHeadCellClass =
    'flex h-10 items-center px-3 py-2 text-[10px] font-medium leading-[14px] uppercase text-[#3d434e]'
  const detailCellClass = 'h-16 px-3 py-2 flex items-center'

  const total = computeChargesTotal(value)
  const configuredRates = taxOptions?.data?.map(rate => rate.rate)
  const taxRates: number[] =
    configuredRates && configuredRates.length > 0
      ? Array.from(new Set(configuredRates))
      : [...TAX_RATES]
  const resolvedQuickPresets: Array<Omit<QuickPreset, 'icon'>> =
    quickPresets ?? []

  const renderQuickPresetVisual = (quickPreset: QuickPreset) => {
    const imageUrl =
      quickPreset.image_url || defaultFeeImageUrl(quickPreset.type)

    return (
      <span className='mr-2 flex size-4 shrink-0 items-center justify-center overflow-hidden'>
        <img
          src={imageUrl}
          alt=''
          className={
            quickPreset.image_url
              ? 'size-full object-cover'
              : 'size-full object-contain'
          }
        />
      </span>
    )
  }

  const addButton = (
    <Button
      type='button'
      variant='outline'
      size='sm'
      className={cn(hideHeading && addButtonClass)}
      onClick={() => (onAddChargeClick ? onAddChargeClick() : add())}
      disabled={disabled}
    >
      <Plus className='mr-2 h-4 w-4' />{' '}
      {hideHeading
        ? t('optionalCharges.addFee')
        : t('optionalCharges.addCharge')}
    </Button>
  )

  const totalFeeRow = hideHeading ? (
    <div className='flex w-full justify-end overflow-hidden border-y border-border'>
      <div className='flex min-w-0 flex-1 flex-col'>
        <div className='flex h-14 items-center justify-end border-b border-border bg-[#f9f9f9] px-3 py-4'>
          <span className='text-base font-semibold text-foreground'>
            {t('optionalCharges.totalFee')}
          </span>
        </div>
      </div>
      <div className='flex w-[200px] shrink-0 flex-col'>
        <div className='flex h-14 items-center justify-end border-b border-border bg-[#f9f9f9] pl-3 pr-14 py-4'>
          <span className='text-base font-semibold text-foreground'>
            {fmt(total)}
          </span>
        </div>
      </div>
    </div>
  ) : (
    <div className='ml-auto flex w-full max-w-xs justify-between border-t border-border pt-2 text-sm font-medium'>
      <span className='text-muted-foreground'>
        {t('optionalCharges.total')}
      </span>
      <span>{fmt(total)}</span>
    </div>
  )

  if (hideHeading) {
    return (
      <div className='space-y-4'>
        {error && <p className='text-sm text-destructive'>{error}</p>}

        <div className='overflow-hidden'>
          <div className='overflow-x-auto'>
            <div className='min-w-[760px]'>
              {value.length > 0 || tradeDiscountRow ? (
                <div className={detailHeaderGridClass}>
                  <div className={detailHeadCellClass}>
                    {t('optionalCharges.feeName')}
                  </div>
                  <div className={detailHeadCellClass}>
                    {t('optionalCharges.amountExcludingVat')}
                  </div>
                  <div className={detailHeadCellClass}>
                    {t('optionalCharges.vatRate')}
                  </div>
                  <div className={detailHeadCellClass}>
                    {t('optionalCharges.lineTotal')}
                  </div>
                  <div className='h-10 px-2 py-2' />
                </div>
              ) : null}

              {value.map((charge, idx) => {
                const fallbackName =
                  charge.type === 'custom'
                    ? ''
                    : t(`optionalCharges.types.${charge.type}`)
                const nameValue = charge.description?.trim()
                  ? charge.description
                  : fallbackName
                const lineAmount = charge.amount || 0
                const chargeVatRate =
                  typeof charge.tax_rate === 'number'
                    ? charge.tax_rate
                    : resolvedVatRate
                const lineTotal = lineAmount * (1 + chargeVatRate / 100)
                const chargeImage =
                  charge.image_url || defaultFeeImageUrl(charge.type)
                const media = (
                  <span className='flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#fff1ea] text-[#ff5a1f]'>
                    <img
                      src={chargeImage}
                      alt=''
                      className={
                        charge.image_url
                          ? 'size-full object-cover object-center'
                          : 'size-4 object-contain'
                      }
                    />
                  </span>
                )

                return (
                  <div key={idx} className={detailGridClass}>
                    <div className={cn(detailCellClass, 'gap-2')}>
                      {media}
                      <Input
                        value={nameValue}
                        placeholder={t('optionalCharges.specifyFeePlaceholder')}
                        maxLength={255}
                        disabled={disabled}
                        className={cn(figmaFieldClass, 'flex-1')}
                        onChange={e =>
                          update(idx, { description: e.target.value })
                        }
                      />
                    </div>

                    <div className={detailCellClass}>
                      <CurrencyNumberInput
                        value={lineAmount}
                        disabled={disabled}
                        aria-label={t('optionalCharges.amount')}
                        className={figmaFieldClass}
                        onValueChange={amount =>
                          update(idx, { amount: amount ?? 0 })
                        }
                      />
                    </div>

                    <div className={detailCellClass}>
                      <Select
                        value={String(chargeVatRate)}
                        onValueChange={nextRate =>
                          update(idx, { tax_rate: Number(nextRate) })
                        }
                        disabled={disabled}
                      >
                        <SelectTrigger
                          className={cn(
                            figmaSelectClass,
                            'w-[119px] justify-between rounded-xl px-3 text-base text-[#171a20]'
                          )}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className='rounded-xl border-[#e7e9ef] p-2'>
                          {taxRates.map(rate => (
                            <SelectItem
                              key={rate}
                              value={String(rate)}
                              className='rounded-lg py-2'
                            >
                              {rate}%
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className={detailCellClass}>
                      <CurrencyNumberInput
                        value={lineTotal}
                        disabled={disabled}
                        aria-label={t('optionalCharges.lineTotal')}
                        className={cn(figmaFieldClass, 'text-[#171a20]')}
                        onValueChange={amount =>
                          update(idx, {
                            amount: computeAmountFromLineTotal(
                              amount ?? 0,
                              chargeVatRate
                            ),
                          })
                        }
                      />
                    </div>

                    <div className='flex h-16 items-center justify-center p-2'>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='size-6 rounded-lg bg-white p-1 text-[#d63b33] hover:bg-[#fff1ea] hover:text-[#d63b33]'
                        title={t('optionalCharges.remove')}
                        disabled={disabled}
                        onClick={() => remove(idx)}
                      >
                        <X className='size-4' />
                      </Button>
                    </div>
                  </div>
                )
              })}

              {tradeDiscountRow ? (
                <div className='grid grid-cols-[minmax(280px,1fr)_144px_144px_144px_40px] items-center border-b border-[#d9f5e9] bg-[#ecfdf5]'>
                  <div className={cn(detailCellClass, 'gap-2')}>
                    <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-white text-[#272c36]'>
                      <Tag className='size-4' />
                    </span>
                    <div className='min-w-0'>
                      <p className='text-base font-semibold uppercase leading-none text-[#272c36]'>
                        {tradeDiscountRow.title}
                      </p>
                      <p className='mt-1 text-sm text-[#555b6a]'>
                        {tradeDiscountRow.description}
                      </p>
                    </div>
                  </div>

                  <div
                    className={cn(
                      detailCellClass,
                      'text-center text-base font-medium text-[#272c36]'
                    )}
                  >
                    -
                  </div>

                  <div
                    className={cn(
                      detailCellClass,
                      'text-center text-base font-medium text-[#272c36]'
                    )}
                  >
                    -
                  </div>

                  <div
                    className={cn(
                      detailCellClass,
                      'text-right text-base font-medium text-[#059669]'
                    )}
                  >
                    -{fmt(tradeDiscountRow.amount)}
                  </div>

                  <div className='flex h-16 items-center justify-center p-2'>
                    {tradeDiscountRow.onRemove ? (
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='size-6 rounded-lg bg-white p-1 text-[#d63b33] hover:bg-[#fff1ea] hover:text-[#d63b33]'
                        title={t('optionalCharges.remove')}
                        onClick={tradeDiscountRow.onRemove}
                      >
                        <X className='size-4' />
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className='flex flex-wrap items-center gap-3'>
          {addButton}

          {resolvedQuickPresets.map(preset => {
            const quickPreset: QuickPreset = {
              id: preset.id,
              type: preset.type,
              label: preset.label,
              amount: preset.amount,
              description: preset.description,
              image_file_id: preset.image_file_id,
              image_url: preset.image_url,
            }

            return (
              <Button
                key={quickPreset.id}
                type='button'
                variant='outline'
                className={quickPresetButtonClass}
                onClick={() =>
                  appendPreset({
                    type: quickPreset.type,
                    amount: quickPreset.amount,
                    description:
                      quickPreset.description ??
                      (typeof quickPreset.amount === 'number' ||
                      quickPreset.type === 'custom'
                        ? quickPreset.label
                        : undefined),
                    image_file_id: quickPreset.image_file_id,
                    image_url: quickPreset.image_url,
                  })
                }
                disabled={disabled}
              >
                {renderQuickPresetVisual(quickPreset)}
                {quickPreset.label}
                {typeof quickPreset.amount === 'number'
                  ? ` ${formatCurrency(quickPreset.amount, 'EUR', Number.isInteger(quickPreset.amount) ? 0 : 2)}`
                  : ''}
              </Button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-3'>
      <div className='flex items-center justify-between'>
        <h3 className='text-sm font-semibold'>
          {t('optionalCharges.heading')}
        </h3>
        {addButton}
      </div>

      {error && <p className='text-sm text-destructive'>{error}</p>}

      {value.length === 0 ? (
        <p className='py-4 text-center text-sm text-muted-foreground'>
          {t('optionalCharges.empty')}
        </p>
      ) : (
        <div className='space-y-2'>
          {value.map((charge, idx) => {
            const isCustom = charge.type === 'custom'
            return (
              <div
                key={idx}
                className='grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-start'
              >
                <Select
                  value={charge.type}
                  onValueChange={v =>
                    update(idx, { type: v as OptionalChargeType })
                  }
                  disabled={disabled}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t('optionalCharges.typePlaceholder')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {OPTIONAL_CHARGE_TYPES.map(type => (
                      <SelectItem key={type} value={type}>
                        {t(`optionalCharges.types.${type}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input
                  value={charge.description ?? ''}
                  placeholder={t('optionalCharges.descriptionPlaceholder')}
                  maxLength={255}
                  disabled={disabled}
                  required={isCustom}
                  aria-required={isCustom}
                  onChange={e => update(idx, { description: e.target.value })}
                />

                <CurrencyNumberInput
                  className='sm:w-32'
                  value={charge.amount}
                  disabled={disabled}
                  aria-label={t('optionalCharges.amount')}
                  onValueChange={amount => update(idx, { amount: amount ?? 0 })}
                />

                {showTaxRate && (
                  <Select
                    value={String(charge.tax_rate ?? defaultTaxRate)}
                    onValueChange={v => update(idx, { tax_rate: Number(v) })}
                    disabled={disabled}
                  >
                    <SelectTrigger className='sm:w-28'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from(
                        new Set([
                          ...taxRates,
                          charge.tax_rate ?? defaultTaxRate,
                        ])
                      )
                        .sort((a, b) => a - b)
                        .map(r => (
                          <SelectItem key={r} value={String(r)}>
                            {r}%
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                )}

                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='h-9 w-9 text-destructive'
                  title={t('optionalCharges.remove')}
                  disabled={disabled}
                  onClick={() => remove(idx)}
                >
                  <Trash2 className='h-4 w-4' />
                </Button>
              </div>
            )
          })}
        </div>
      )}

      {resolvedQuickPresets.length > 0 ? (
        <div className='flex flex-wrap items-center gap-3'>
          {resolvedQuickPresets.map(preset => {
            const quickPreset: QuickPreset = {
              id: preset.id,
              type: preset.type,
              label: preset.label,
              amount: preset.amount,
              description: preset.description,
              image_file_id: preset.image_file_id,
              image_url: preset.image_url,
            }
            return (
              <Button
                key={quickPreset.id}
                type='button'
                variant='outline'
                className={quickPresetButtonClass}
                onClick={() =>
                  appendPreset({
                    type: quickPreset.type,
                    amount: quickPreset.amount,
                    description:
                      quickPreset.description ??
                      (typeof quickPreset.amount === 'number' ||
                      quickPreset.type === 'custom'
                        ? quickPreset.label
                        : undefined),
                    image_file_id: quickPreset.image_file_id,
                    image_url: quickPreset.image_url,
                  })
                }
                disabled={disabled}
              >
                {renderQuickPresetVisual(quickPreset)}
                {quickPreset.label}
                {typeof quickPreset.amount === 'number'
                  ? ` ${formatCurrency(quickPreset.amount, 'EUR', Number.isInteger(quickPreset.amount) ? 0 : 2)}`
                  : ''}
              </Button>
            )
          })}
        </div>
      ) : null}
      {totalFeeRow}
    </div>
  )
}

export default OptionalChargesSection
